import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const transporterBenefits = [
  'List available space by route, date, and vehicle type',
  'Earn from otherwise idle return trips and partial loads',
  'Get matched to nearby loads — warehouse-to-market, market-to-market',
  'Transparent per-route pricing, no long-term contracts',
];

const shipperBenefits = [
  'Search available cold-chain capacity by route and date',
  'Book verified transporters with temperature-logging',
  'Track your produce in transit — real-time temperature monitoring',
  'Insurance and liability covered on every load',
];

export default function MarketplaceSection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-semibold text-secondary-600 uppercase tracking-widest mb-4">
            Two-Sided Marketplace
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 mb-4">
            Built for Both Sides of the Cold Chain
          </h2>
          <p className="text-foreground-500 max-w-xl mx-auto">
            Whether you own a reefer van or need to move temperature-sensitive produce — the marketplace works for you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Transporter card */}
          <div className="group bg-card-50 rounded-xl p-8 md:p-10 border-t-2 border-secondary-500 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 card-animate">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 animate-icon-float icon-hover-pop">
                <i className="ri-truck-line text-2xl"></i>
              </div>
              <div>
                <h3 className="font-heading font-bold text-xl text-primary-700">Have a Reefer Van?</h3>
                <p className="text-sm text-foreground-400">Turn idle capacity into revenue</p>
              </div>
            </div>
            <div className="space-y-3 mb-6">
              {transporterBenefits.map((b) => (
                <div key={b} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mt-0.5">
                    <i className="ri-check-line text-xs"></i>
                  </div>
                  <span className="text-sm text-foreground-600">{b}</span>
                </div>
              ))}
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-500 hover:bg-secondary-600 text-paper-50 font-semibold text-sm rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Register Your Vehicle
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* Shipper card */}
          <div className="group bg-card-50 rounded-xl p-8 md:p-10 border-t-2 border-accent-500 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 card-animate">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 animate-icon-float icon-hover-pop">
                <i className="ri-map-pin-line text-2xl"></i>
              </div>
              <div>
                <h3 className="font-heading font-bold text-xl text-primary-700">Need to Move Produce?</h3>
                <p className="text-sm text-foreground-400">Book verified cold-chain capacity</p>
              </div>
            </div>
            <div className="space-y-3 mb-6">
              {shipperBenefits.map((b) => (
                <div key={b} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mt-0.5">
                    <i className="ri-check-line text-xs"></i>
                  </div>
                  <span className="text-sm text-foreground-600">{b}</span>
                </div>
              ))}
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 hover:bg-accent-600 text-brand-800 font-semibold text-sm rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Book Transport
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}